import localforage from 'localforage';
import { EVERY_MINUTE, type AppBackgroundJobs } from '../constants';

export interface JobContext {
    lastExecutedAt?: number; // ms since epoch
}

interface HandlerOption {
    name: AppBackgroundJobs;
    timeInMinutes: number;
    handler: (context?: JobContext) => Promise<void>;
}

type Handlers = Record<string, HandlerOption>;

type BackgroundJobTimestamps = Record<string, number | undefined>;

export const BACKGROUND_JOB_TIMESTAMP_KEY = 'background-job-keys';

export class BackgroundJob {
    readonly handlers: Handlers = {};

    registerJob(handler: HandlerOption) {
        this.handlers[handler.name] = handler;
    }

    unRegisterJob(handlerName: AppBackgroundJobs) {
        if (!Object.prototype.hasOwnProperty.call(this.handlers, handlerName)) {
            return;
        }

        Reflect.deleteProperty(this.handlers, handlerName);
    }

    subscribe() {
        if (this.intervalRef) {
            return;
        }

        this.intervalRef = setInterval(() => {
            this.handleInterval();
        }, 15000);
    }

    unsubscribe() {
        clearInterval(this.intervalRef);
        this.intervalRef = undefined;
    }

    async runJobManually(jobName: AppBackgroundJobs) {
        const handler = Object.prototype.hasOwnProperty.call(
            this.handlers,
            jobName,
        )
            ? Reflect.get(this.handlers, jobName)
            : undefined;

        if (!handler) {
            throw new Error(`Job ${jobName} not found`);
        }

        const { [jobName]: lastExecutedAt } = await this.getJobTimestamps();

        try {
            await handler.handler({ lastExecutedAt });
            const freshTimestamps = await this.getJobTimestamps();
            await this.updateJobTimestamps({
                ...freshTimestamps,
                [jobName]: Date.now(),
            });
        } catch (error) {
            console.error('Error in manual job execution:', error);
            throw error;
        }
    }

    private intervalRef: NodeJS.Timeout | undefined;

    private async handleInterval() {
        const timestamps = await this.getJobTimestamps();

        for (const _field in this.handlers) {
            const fieldKey = _field as AppBackgroundJobs;
            const handler: HandlerOption = Reflect.get(this.handlers, fieldKey);

            const lastExecutedAt: number | undefined =
                Object.prototype.hasOwnProperty.call(timestamps, fieldKey)
                    ? Reflect.get(timestamps, fieldKey)
                    : undefined;
            const shouldRun = this.checkJobStatus(
                lastExecutedAt,
                handler.timeInMinutes,
            );

            if (!shouldRun) {
                continue;
            }

            try {
                await handler.handler({ lastExecutedAt });
                await this.updateJobTimestamps({
                    ...timestamps,
                    [fieldKey]: Date.now(),
                });
            } catch (error) {
                console.error('Error in recurring job handler:', error);
            }
        }
    }

    private async getJobTimestamps() {
        const stored = await localforage.getItem<BackgroundJobTimestamps>(
            BACKGROUND_JOB_TIMESTAMP_KEY,
        );
        return stored ?? {};
    }

    private async updateJobTimestamps(data: BackgroundJobTimestamps) {
        return await localforage.setItem(BACKGROUND_JOB_TIMESTAMP_KEY, data);
    }

    private checkJobStatus(
        lastExecutedAt: number | undefined,
        timeInMinutes: number,
    ) {
        const threshold = timeInMinutes * EVERY_MINUTE;

        if (!lastExecutedAt) {
            return true;
        }

        const timeElapsed = Date.now() - lastExecutedAt;

        if (timeElapsed >= threshold) {
            return true;
        }

        return false;
    }
}

export const backgroundJob = new BackgroundJob();
